import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { MessageCircleQuestion, Send } from 'lucide-react';
import {
  askAiQuestion,
  getMyAiQuestions,
  getPublicAiQuestionSetting,
  AiQuestionMine,
  PublicAiQuestionSetting,
} from '../../api/payment/ai-question.api';
import { AI_QUESTION_MAX, questionStatusLabel } from '../../utils/ai-question';
import { formatCa } from '../../utils/donate-alert';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';
import { LocalStorageService, RootState } from '../../configuration';
import { routePaths } from '../../routes/routes-path';

/**
 * "Hỏi AI trên live" hiển thị cho NGƯỜI XEM trên trang creator, trong tab Donate — người xem
 * gõ câu hỏi (tối đa 200 ký tự) rồi gửi kèm Ca. Chỉ hiện khi creator đã bật tính năng và
 * không phải trang cá nhân của chính người xem.
 */
const AiQuestionAsk: React.FC<{
  userId?: string;
  onAvailableChange?: (available: boolean) => void;
}> = ({ userId, onAvailableChange }) => {
  const loggedIn = !!LocalStorageService.getItem('accessToken');
  const myProfile = useSelector((state: RootState) => state.features.auth.profile);
  const isOwner = !!myProfile?.userId && !!userId && myProfile.userId === userId;

  const [setting, setSetting] = useState<PublicAiQuestionSetting | null>(null);
  const [question, setQuestion] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState<AiQuestionMine[]>([]);
  const [visible, setVisible] = useState(document.visibilityState === 'visible');

  useEffect(() => {
    if (!userId) return;
    getPublicAiQuestionSetting(userId)
      .then(setSetting)
      .catch(() => setSetting(null));
  }, [userId]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const loadMine = useCallback(() => {
    if (!loggedIn || !userId) return;
    getMyAiQuestions(userId)
      .then(setMine)
      .catch(() => {
        /* lỗi tải danh sách của tôi -> giữ nguyên danh sách cũ, không chặn form gửi câu hỏi */
      });
  }, [loggedIn, userId]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  usePollWhile(loggedIn && !!setting?.enabled && visible, loadMine, 15000);

  // Chủ kênh xem trang cá nhân của chính mình -> không hiện khối gửi câu hỏi (không tự hỏi mình).
  const available = !!setting?.enabled && !isOwner;

  // Báo lên cha (thanh tab Donate) biết tính năng này có đang hiện hay không, để ẩn/hiện tab
  // tương ứng. Dùng ref giữ callback mới nhất để effect chỉ chạy lại khi `available` đổi.
  const onAvailableChangeRef = useRef(onAvailableChange);
  onAvailableChangeRef.current = onAvailableChange;
  useEffect(() => {
    onAvailableChangeRef.current?.(available);
    // Unmount (vd đổi tab lớn rồi quay lại) -> báo về false, tránh cha giữ mãi trạng thái
    // available cũ (component đã biến mất nhưng cha vẫn tưởng tab này còn hiện được).
    return () => onAvailableChangeRef.current?.(false);
  }, [available]);

  if (!available || !setting) return null;

  const trimmedLen = question.trim().length;
  const canSubmit = trimmedLen > 0 && trimmedLen <= AI_QUESTION_MAX;

  const handleSubmit = () => {
    if (submitting || !canSubmit || !userId) return;
    setSubmitting(true);
    askAiQuestion({
      creatorId: userId,
      question: question.trim(),
      isAnonymous,
    })
      .then(() => {
        toast.success('Đã gửi câu hỏi');
        setQuestion('');
        setIsAnonymous(false);
        loadMine();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Gửi câu hỏi thất bại');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="mt-8 ui-card p-4 md:p-6">
      <div className="flex items-center gap-2">
        <MessageCircleQuestion className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold ui-heading">Hỏi AI trên live</h3>
      </div>

      <p className="mt-2 text-sm ui-muted">
        Gửi câu hỏi, AI sẽ viết câu trả lời để streamer đọc trên live.
      </p>

      <div className="mt-3 flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, AI_QUESTION_MAX + 20))}
            placeholder="Nhập câu hỏi của bạn..."
            className="ui-input min-h-[80px] w-full px-3 py-2 text-base"
          />
          <span
            className={`self-end text-xs ${
              trimmedLen > AI_QUESTION_MAX ? 'text-red-500' : 'ui-muted'
            }`}
          >
            {trimmedLen}/{AI_QUESTION_MAX}
          </span>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none text-sm ui-text">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          Ẩn danh
        </label>

        {loggedIn ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send size={16} />
            {submitting ? 'Đang gửi...' : `Gửi câu hỏi · ${formatCa(setting.priceCa)}`}
          </button>
        ) : (
          <Link
            to={routePaths.login}
            className="flex items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-base text-white"
          >
            Đăng nhập để gửi câu hỏi
          </Link>
        )}

        <p className="text-xs ui-muted">
          Bạn chỉ bị trừ Ca khi streamer phát câu hỏi lên live.
        </p>
      </div>

      {mine.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          <span className="text-sm font-medium ui-text">Câu hỏi của bạn</span>
          {mine.map((q) => (
            <div
              key={q.id}
              className="flex flex-col gap-1 rounded-lg border ui-divider ui-surface p-2 text-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate ui-text">{q.question}</span>
                <span className="shrink-0 text-xs ui-muted">
                  {questionStatusLabel(q.status)}
                </span>
              </div>
              {q.status === 'Played' && q.answer && (
                <p className="text-xs ui-muted">{q.answer}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AiQuestionAsk;
